<script setup lang="ts">
/**
 * Skeleton de chargement partagé entre les pages publiques /a/[slug] et /b/[slug].
 *
 * Calqué sur la STRUCTURE RÉELLE de la page une fois data chargées :
 *  - max-width: 1280px, padding 3/6 (= max-w-[1280px] mx-auto px-3 md:px-6)
 *  - 2 colonnes : flex-1 lg:w-3/4 + sidebar lg:w-[320px], gap 4/6
 *  - Colonne gauche commence par une card header blanche rounded-3xl
 *    (badge catégorie + titre + ligne « par {org} » + bouton partager + date + description)
 *    → PAS DE HERO IMAGE plein écran (le skeleton précédent en faisait un erroné)
 *  - Suivie d'une 2e card blanche (wizard de checkout : tabs + contenu step 1)
 *  - Sidebar : card location avec mini-map 180px + adresse + bloc contact
 *
 * Karim 2026-06-27 : refonte pour fidélité visuelle au layout réel
 * (event ET adhésion partagent quasi le même squelette).
 */
import { useDemoAccueil } from "~/composables/useDemoAccueil";

// Démo de la home : la page s'y affiche sans son en-tête (la fenêtre de
// navigateur simulée en tient lieu) — son squelette non plus.
const { estIntegree } = useDemoAccueil();

defineProps<{
  /** Rayon de l'en-tête — le même que la page chargée, sans saut au rendu. */
  rayon?: "evenements" | "adhesions" | "cagnottes";
}>();
</script>

<template>
  <div class="min-h-screen flex flex-col bg-[#f7f9fd] dark:bg-[#0a0a0a]">
    <AppHeader v-if="!estIntegree" :rayon="rayon" />
    <div class="flex-1 max-w-[1280px] mx-auto w-full px-3 md:px-6 pt-32 pb-16">
      <div class="flex flex-col lg:flex-row gap-4 md:gap-6">

        <!-- LEFT COLUMN — header card + wizard card -->
        <div class="flex-1 lg:w-3/4 space-y-4 md:space-y-6">

          <!-- Header card : badge + titre + "par {org}" + bouton + date + description -->
          <div class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] p-4 md:p-6">
            <div class="flex items-start justify-between gap-4">
              <div class="flex-1 min-w-0 space-y-3">
                <!-- Badge catégorie -->
                <div class="h-5 w-20 rounded-full bg-gray-200 dark:bg-gray-800 animate-pulse" />
                <!-- Titre -->
                <div class="h-8 md:h-9 w-3/4 rounded-xl bg-gray-200 dark:bg-gray-800 animate-pulse" />
                <!-- "par {org}" -->
                <div class="h-4 w-1/3 rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse" />
              </div>
              <!-- Bouton Partager -->
              <div class="h-9 md:h-10 w-9 md:w-28 rounded-xl bg-gray-200 dark:bg-gray-800 animate-pulse shrink-0" />
            </div>

            <!-- Date row -->
            <div class="mt-4 flex items-center gap-2">
              <div class="h-4 w-4 rounded bg-gray-200 dark:bg-gray-800 animate-pulse" />
              <div class="h-4 w-2/5 rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse" />
            </div>

            <!-- Description (3 lignes) -->
            <div class="mt-5 space-y-2">
              <div class="h-3.5 w-full rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse" />
              <div class="h-3.5 w-11/12 rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse" />
              <div class="h-3.5 w-3/4 rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse" />
            </div>
          </div>

          <!-- Wizard card : tabs + contenu step 1 (sélection tarifs / billets) -->
          <div class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] overflow-hidden">
            <!-- Tabs row -->
            <div class="flex items-center border-b border-gray-100 dark:border-gray-800">
              <div class="flex-1 flex items-center justify-center gap-2 py-3 md:py-4">
                <div class="h-5 w-5 rounded-full bg-gray-200 dark:bg-gray-800 animate-pulse" />
                <div class="h-3.5 w-16 rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse" />
              </div>
              <div class="flex-1 flex items-center justify-center gap-2 py-3 md:py-4">
                <div class="h-5 w-5 rounded-full bg-gray-200 dark:bg-gray-800 animate-pulse" />
                <div class="h-3.5 w-20 rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse" />
              </div>
              <div class="flex-1 flex items-center justify-center gap-2 py-3 md:py-4">
                <div class="h-5 w-5 rounded-full bg-gray-200 dark:bg-gray-800 animate-pulse" />
                <div class="h-3.5 w-16 rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse" />
              </div>
            </div>

            <!-- Contenu step (3 tarifs simulés) -->
            <div class="p-4 md:p-6 space-y-3">
              <div
                v-for="i in 3"
                :key="i"
                class="flex items-center justify-between p-4 rounded-2xl border border-gray-100 dark:border-gray-800"
              >
                <div class="flex-1 space-y-2">
                  <div class="h-4 w-1/3 rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse" />
                  <div class="h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-800 animate-pulse" />
                </div>
                <div class="h-9 w-24 rounded-xl bg-gray-200 dark:bg-gray-800 animate-pulse shrink-0" />
              </div>
            </div>
          </div>
        </div>

        <!-- SIDEBAR (320px) — location + contact -->
        <div class="lg:w-[320px] shrink-0">
          <div class="space-y-4 md:space-y-6">
            <!-- Location card : mini-map 180px + adresse + contact -->
            <div class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] overflow-hidden">
              <div class="h-[180px] bg-gray-200 dark:bg-gray-800 animate-pulse" />
              <div class="p-5 space-y-2">
                <div class="h-4 w-2/3 rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse" />
                <div class="h-3.5 w-full rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse" />
                <div class="h-3.5 w-1/2 rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse" />
              </div>
              <div class="border-t border-gray-100 dark:border-gray-800 p-5 space-y-2">
                <div class="h-3 w-1/3 rounded bg-gray-200 dark:bg-gray-800 animate-pulse" />
                <div class="h-3.5 w-3/4 rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
